import { forwardRef, PropsWithChildren } from 'react'
import { Button, ButtonProps, cn } from '@wellesley/ui'
import { LucideIcon } from 'lucide-react'

type ToolbarButtonProps = Omit<ButtonProps, 'onClick'> &
  PropsWithChildren<{
    ariaLabel: string
    Icon?: LucideIcon
    onClick?: () => void
    shortcut?: string
  }>

const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  (
    { ariaLabel, Icon, onClick, shortcut, className, children, ...rest },
    ref,
  ) => {
    const title = `${ariaLabel}${shortcut ? ` (${formatShortcut(shortcut)})` : ''}`

    return (
      <Button
        ref={ref}
        variant="tertiary"
        size="icon_s"
        type="button"
        aria-label={ariaLabel}
        title={title}
        className={cn(className, 'rounded-sm text-gray-500 hover:bg-gray-50')}
        onClick={() => onClick?.()}
        {...rest}
      >
        {Icon ? <Icon size={16} /> : children}
      </Button>
    )
  },
)

ToolbarButton.displayName = 'ToolbarButton'

const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPod|iPhone|iPad/.test(navigator.platform)

const formatShortcut = (shortcut?: string): string => {
  if (!shortcut) return ''
  const parts = shortcut.split('+').map((p) => p.trim())
  const mapped = parts.map((p) => {
    const key = p.toLowerCase()
    if (key === 'mod') return isMac ? '⌘' : 'Ctrl'
    if (key === 'shift') return isMac ? '⇧' : 'Shift'
    if (key === 'alt' || key === 'option') return isMac ? '⌥' : 'Alt'
    if (key === 'ctrl' || key === 'control') return isMac ? '⌃' : 'Ctrl'

    if (p.length === 1) return p.toUpperCase()

    return p.charAt(0).toUpperCase() + p.slice(1)
  })

  return mapped.join(isMac ? '' : '+')
}

export { ToolbarButton }
